// Currency Conversion Utilities

// Base currency (USD)
const BASE_CURRENCY = 'USD';

// Fallback exchange rates (updated periodically)
// In production, you would fetch these from a real API
const FALLBACK_RATES = {
  'USD': 1.0,
  'EUR': 0.85,
  'GBP': 0.73,
  'CAD': 1.35,
  'AUD': 1.45,
  'JPY': 110.0,
  'CNY': 6.45,
  'INR': 74.0,
  'BRL': 5.2,
  'MXN': 20.0,
  'ZAR': 14.5,
  'NGN': 1488.75,
  'KES': 110.0,
  'GHS': 6.0,
  'EGP': 15.7,
  'MAD': 9.0,
  'DZD': 135.0,
  'TND': 2.8,
  'LYD': 4.5,
  'SDG': 55.0,
  'ETB': 44.0,
  'UGX': 3500.0,
  'TZS': 2300.0,
  'ZWL': 85.0,
  'BWP': 10.8,
  'ZMW': 18.0,
  'MWK': 820.0,
  'MZN': 63.0,
  'AOA': 650.0,
  'MGA': 3900.0,
  'MUR': 42.0,
  'SCR': 13.5,
  'KMF': 430.0,
  'DJF': 178.0,
  'SOS': 580.0,
  'ERN': 15.0,
  'SSP': 55.0,
  'XAF': 560.0,
  'CDF': 2000.0,
  'STN': 21.0,
  'XOF': 560.0,
  'GNF': 9800.0,
  'SLE': 10.5,
  'LRD': 150.0,
  'MRU': 36.0,
  'GMD': 52.0,
  'CVE': 97.0
};

// Cache for exchange rates
let exchangeRatesCache = null;
let lastCacheUpdate = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Fetch live exchange rates from a free API
export const fetchExchangeRates = async () => {
  try {
    // Try multiple free APIs for redundancy
    const apis = [
      'https://api.exchangerate-api.com/v4/latest/USD',
      'https://api.fixer.io/latest?access_key=free&base=USD',
      'https://open.er-api.com/v6/latest/USD'
    ];

    for (const apiUrl of apis) {
      try {
        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          
          // Handle different API response formats
          let rates = {};
          if (data.rates) {
            rates = data.rates;
          } else if (data.data && data.data.rates) {
            rates = data.data.rates;
          }

          if (Object.keys(rates).length > 0) {
            // Ensure USD is included
            rates.USD = 1.0;
            return rates;
          }
        }
      } catch (error) {
        console.log(`API ${apiUrl} failed:`, error.message);
        continue;
      }
    }

    // If all APIs fail, return fallback rates
    console.warn('All exchange rate APIs failed, using fallback rates');
    return FALLBACK_RATES;
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    return FALLBACK_RATES;
  }
};

// Get exchange rates (with caching)
export const getExchangeRates = async () => {
  const now = Date.now();
  
  // Return cached rates if still valid
  if (exchangeRatesCache && lastCacheUpdate && (now - lastCacheUpdate) < CACHE_DURATION) {
    return exchangeRatesCache;
  }

  // Fetch new rates
  const rates = await fetchExchangeRates();
  exchangeRatesCache = rates;
  lastCacheUpdate = now;
  
  return rates;
};

// Convert amount from one currency to another
export const convertCurrency = async (amount, fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  try {
    const rates = await getExchangeRates();
    
    // Convert to USD first if not already USD
    let usdAmount = amount;
    if (fromCurrency !== BASE_CURRENCY) {
      const fromRate = rates[fromCurrency];
      if (!fromRate) {
        throw new Error(`Exchange rate not found for ${fromCurrency}`);
      }
      usdAmount = amount / fromRate;
    }

    // Convert from USD to target currency
    let convertedAmount = usdAmount;
    if (toCurrency !== BASE_CURRENCY) {
      const toRate = rates[toCurrency];
      if (!toRate) {
        throw new Error(`Exchange rate not found for ${toCurrency}`);
      }
      convertedAmount = usdAmount * toRate;
    }

    return convertedAmount;
  } catch (error) {
    console.error('Currency conversion error:', error);
    // Fallback to simple conversion using cached rates
    const rates = FALLBACK_RATES;
    let usdAmount = amount;
    if (fromCurrency !== BASE_CURRENCY && rates[fromCurrency]) {
      usdAmount = amount / rates[fromCurrency];
    }
    if (toCurrency !== BASE_CURRENCY && rates[toCurrency]) {
      return usdAmount * rates[toCurrency];
    }
    return amount;
  }
};

// Get conversion rate between two currencies
export const getConversionRate = async (fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) {
    return 1.0;
  }

  try {
    const rates = await getExchangeRates();
    
    if (fromCurrency === BASE_CURRENCY) {
      return rates[toCurrency] || 1.0;
    }
    
    if (toCurrency === BASE_CURRENCY) {
      return 1.0 / (rates[fromCurrency] || 1.0);
    }
    
    // Convert through USD
    const fromRate = rates[fromCurrency] || 1.0;
    const toRate = rates[toCurrency] || 1.0;
    return toRate / fromRate;
  } catch (error) {
    console.error('Error getting conversion rate:', error);
    return 1.0;
  }
};

// Format converted amount with currency symbol
export const formatConvertedAmount = (amount, currency, precision = 2) => {
  const { formatCurrency } = require('./geolocation');
  return formatCurrency(amount, currency);
};

// Get suggested amounts in user's currency
export const getSuggestedAmounts = async (currency, baseAmounts = [10, 25, 50, 100, 250, 500]) => {
  try {
    const convertedAmounts = [];
    
    for (const amount of baseAmounts) {
      const converted = await convertCurrency(amount, 'USD', currency);
      convertedAmounts.push({
        original: amount,
        converted: Math.round(converted),
        currency
      });
    }
    
    return convertedAmounts;
  } catch (error) {
    console.error('Error getting suggested amounts:', error);
    return baseAmounts.map(amount => ({
      original: amount,
      converted: amount,
      currency: 'USD'
    }));
  }
};

// Clear exchange rate cache (useful for testing)
export const clearExchangeRateCache = () => {
  exchangeRatesCache = null;
  lastCacheUpdate = null;
};
